import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Search, RefreshCw, Copy, ExternalLink } from 'lucide-react';
import { useMerchantAuth } from '../../contexts/MerchantAuthContext';
import MerchantLayout from '../../components/MerchantLayout';
import { toast } from 'sonner';

const statusColors = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  FUNDED_DETECTED: 'bg-blue-100 text-blue-800',
  FORWARDED: 'bg-green-100 text-green-800',
  EXPIRED: 'bg-gray-100 text-gray-800',
  CANCELLED: 'bg-red-100 text-red-800'
};

const statusLabels = {
  PENDING: 'Pendiente',
  FUNDED_DETECTED: 'Fondeada',
  FORWARDED: 'Completada',
  EXPIRED: 'Expirada',
  CANCELLED: 'Cancelada'
};

export default function MerchantOrdersPage() {
  const { fetchWithAuth } = useMerchantAuth();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    filterOrders();
  }, [orders, searchTerm, filterStatus]);

  const fetchOrders = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetchWithAuth(`${backendUrl}/api/merchant/orders?limit=100`);
      
      if (response.ok) {
        const data = await response.json();
        setOrders(data);
      } else {
        throw new Error('Error fetching orders');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar órdenes');
      setLoading(false);
    }
  };

  const filterOrders = () => {
    let filtered = orders;

    if (filterStatus !== 'ALL') {
      filtered = filtered.filter(order => order.status === filterStatus);
    }

    if (searchTerm) {
      filtered = filtered.filter(order =>
        order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.deposit_address.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredOrders(filtered);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <MerchantLayout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[hsl(var(--brand-teal))] mx-auto"></div>
          <p className="mt-4 text-[hsl(var(--muted-foreground))]">Cargando órdenes...</p>
        </div>
      </MerchantLayout>
    );
  }

  return (
    <MerchantLayout>
      <div className="space-y-6" data-testid="merchant-orders-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-[hsl(var(--brand-ink))]" style={{ fontFamily: 'var(--font-heading)' }}>
              Mis Órdenes
            </h1>
            <p className="text-[hsl(var(--muted-foreground))] mt-1">
              Todas tus transacciones en un solo lugar
            </p>
          </div>
          <Button onClick={fetchOrders} variant="outline" size="sm" data-testid="merchant-refresh-orders-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                <Input
                  placeholder="Buscar por ID o dirección..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="merchant-search-orders-input"
                />
              </div>
            </div>
            <div className="flex gap-2">
              {['ALL', 'PENDING', 'FUNDED_DETECTED', 'FORWARDED', 'EXPIRED'].map(status => (
                <Button
                  key={status}
                  variant={filterStatus === status ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus(status)}
                  data-testid={`merchant-filter-${status.toLowerCase()}`}
                >
                  {status === 'ALL' ? 'Todas' : statusLabels[status]}
                </Button>
              ))}
            </div>
          </div>
        </Card>

        {/* Orders Table */}
        {filteredOrders.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-lg text-[hsl(var(--muted-foreground))] mb-4">
              {searchTerm || filterStatus !== 'ALL' ? 'No se encontraron órdenes con esos filtros' : 'No tienes órdenes aún'}
            </p>
            {!searchTerm && filterStatus === 'ALL' && (
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                Crea tu primera orden usando la API
              </p>
            )}
          </Card>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="merchant-orders-table">
                <thead className="bg-[hsl(var(--accent))] border-b">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">ID</th>
                    <th className="text-left p-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Monto</th>
                    <th className="text-left p-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Status</th>
                    <th className="text-left p-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Fecha</th>
                    <th className="text-left p-4 text-sm font-medium text-[hsl(var(--muted-foreground))]">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => (
                    <tr key={order.id} className="border-b hover:bg-[hsl(var(--accent))]/50 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <code className="text-xs bg-[hsl(var(--accent))] px-2 py-1 rounded">
                            {order.id.substring(0, 8)}...
                          </code>
                          <button onClick={() => copyToClipboard(order.id)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--brand-teal))]">
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="font-medium">{order.amount_crypto} {order.crypto_asset}</p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">
                          ${order.amount_fiat} USD
                        </p>
                      </td>
                      <td className="p-4">
                        <Badge className={statusColors[order.status]}>
                          {statusLabels[order.status]}
                        </Badge>
                      </td>
                      <td className="p-4 text-sm text-[hsl(var(--muted-foreground))]">
                        {formatDate(order.created_at)}
                      </td>
                      <td className="p-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`/payment/${order.id}`, '_blank')}
                          data-testid={`merchant-view-order-${order.id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        <div className="text-sm text-[hsl(var(--muted-foreground))] text-center">
          Mostrando {filteredOrders.length} de {orders.length} órdenes totales
        </div>
      </div>
    </MerchantLayout>
  );
}
